/**
 * The tracker dogs on screen (ROUND8 §3): every `world.enemies` entry of kind `dog`, drawn on the dog rig instead
 * of a soldier's. `Actors` hands them here as it hands machines to `Machines`, and passes on the frame's events
 * so each bark opens the jaw of the dog that gave it. Positions are interpolated between ticks and texel-locked as
 * the men's are; the pose is chosen by `chooseDogPose` from the sim's mode and the dog's speed.
 *
 * What the state alone cannot say, this class remembers per dog: how far it has walked (its gait runs on that, so
 * the paws do not slide whatever the sim counts), how long it has stood still (a dog at heel sits), when it last
 * barked, and when it went down - a dog seen alive and then dead plays its fall; one first met dead just lies there.
 */

import type { Material, Scene } from 'three';
import { isDog, speedOf } from '../../sim/enemies/specials/kinds';
import { DOGS } from '../../sim/tuningDogs';
import type { Enemy, SimEvent, World } from '../../sim/types';
import type { PixelCamera } from '../camera/PixelCamera';
import type { Palette } from '../palette';
import { Dog } from './dog';
import { chooseDogPose, dogDown, MOVING_ABOVE } from './dogMotion';
import { DOG_DEATH_SECONDS } from './dogPoses';

/** Baying with no bark heard for this long, seconds, the jaw keeps the sim's rhythm on its own clock. */
const BARK_SILENCE = DOGS.barkEverySeconds * 2;

interface DogView {
  dog: Dog;
  x: number;
  z: number;
  travelled: number;
  stillSince: number;
  /** Render time of its last bark; -Infinity for never. */
  barkedAt: number;
  /** Render time it went down; -Infinity when it was already dead when first seen. */
  fellAt: number;
  down: boolean;
}

export class Dogs {
  private readonly views = new Map<number, DogView>();
  /** Barks heard since the last `update`: they take their time from it, as `Actors`' one-shots do. */
  private readonly barks = new Set<number>();

  constructor(
    private readonly scene: Scene,
    private readonly palette: Palette,
    /** The enemies' material: a dog takes the same ink outline as the men it works with (05 §8). */
    private readonly material: Material,
    private readonly groundY: (x: number, z: number) => number,
  ) {}

  /** This frame's sim events, before `update`: which dogs gave tongue. */
  handle(events: readonly SimEvent[]): void {
    for (const e of events) if (e.type === 'bark' && e.kind === 'dogBark') this.barks.add(e.id);
  }

  private view(e: Enemy, x: number, z: number, time: number): DogView {
    let v = this.views.get(e.id);
    if (v) return v;
    // Built the first time it is seen: most missions have no dogs at all.
    const dog = new Dog(this.palette, this.material);
    this.scene.add(dog.mesh);
    const down = dogDown(e);
    v = {
      dog, x, z, travelled: 0, stillSince: time, barkedAt: Number.NEGATIVE_INFINITY,
      fellAt: Number.NEGATIVE_INFINITY, down,
    };
    this.views.set(e.id, v);
    return v;
  }

  update(world: World, time: number, alpha: number, camera: PixelCamera): void {
    const seen = new Set<number>();
    for (const e of world.enemies) {
      if (!isDog(e.kind)) continue;
      seen.add(e.id);
      const x = e.prevX + (e.x - e.prevX) * alpha;
      const z = e.prevZ + (e.z - e.prevZ) * alpha;
      const v = this.view(e, x, z, time);
      v.dog.mesh.visible = true;
      v.travelled += Math.hypot(x - v.x, z - v.z);
      v.x = x;
      v.z = z;
      if (this.barks.has(e.id)) v.barkedAt = time;
      this.pose(v, e, time);
      v.dog.state.position.set(x, this.groundY(x, z), z);
      v.dog.state.facing = e.facing;
      v.dog.update(time, camera);
    }
    this.barks.clear();
    // A dog the sim has taken away stops being drawn.
    for (const [id, v] of this.views) if (!seen.has(id)) v.dog.mesh.visible = false;
  }

  private pose(v: DogView, e: Enemy, time: number): void {
    const s = v.dog.state;
    s.travelled = v.travelled;
    if (dogDown(e)) {
      if (!v.down) v.fellAt = time;
      v.down = true;
      const age = time - v.fellAt;
      s.motion = age < DOG_DEATH_SECONDS ? 'death' : 'dead';
      s.age = age;
      return;
    }
    v.down = false;
    const speed = speedOf(e);
    if (speed > MOVING_ABOVE) v.stillSince = time;
    const choice = chooseDogPose(e, speed, time - v.stillSince);
    s.motion = choice.motion;
    s.head = choice.head;
    s.sinceBark = this.sinceBark(v, time);
  }

  /**
   * Seconds since its last bark, from the events. A baying dog that has not been heard for a while (no events are
   * handed in: a tool, a test) barks on its own clock at the sim's rate, so the tell never freezes shut.
   */
  private sinceBark(v: DogView, time: number): number {
    const heard = time - v.barkedAt;
    return heard < BARK_SILENCE ? heard : time % DOGS.barkEverySeconds;
  }

  dispose(): void {
    for (const v of this.views.values()) v.dog.dispose();
    this.views.clear();
    this.barks.clear();
  }
}
